import { Boxes, Package, Percent, Wallet } from "lucide-react";
import { Suspense, lazy, type ComponentType } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { defineFeature } from "../types";

/**
 * Catalog — the service catalog, packages, GST rates and rate cards, mounted as
 * four sections inside /settings (PRD US-F4.7, §5.1).
 *
 * This is the price list the rest of the product reads from: leads size an
 * opportunity with it, proposals quote from it, engagements bill from it and
 * invoice lines are built out of it. It lives in Settings rather than in the
 * main nav because it is something an agency configures a few times a year and
 * then quotes from every day.
 *
 * Every page is `React.lazy`, so this module costs the entry bundle nothing but
 * its icons and section metadata.
 */

const ServicesPage = lazy(() => import("@/app/settings/catalog/services"));
const PackagesPage = lazy(() => import("@/app/settings/catalog/packages"));
const TaxRatesPage = lazy(() => import("@/app/settings/catalog/tax-rates"));
const RateCardsPage = lazy(() => import("@/app/settings/catalog/rate-cards"));

function SectionFallback() {
  return (
    <div className="grid w-full gap-5" role="status" aria-label="Loading the catalog">
      <div className="grid gap-2">
        <Skeleton className="h-6 w-56" />
        <Skeleton className="h-4 w-96" />
      </div>
      <Skeleton className="h-10 w-full" rounded="pill" />
      <Skeleton className="h-72 w-full" rounded="card" />
    </div>
  );
}

function section(Page: ComponentType) {
  return (
    <Suspense fallback={<SectionFallback />}>
      <Page />
    </Suspense>
  );
}

export default defineFeature({
  key: "catalog",
  title: "Service catalog",
  entities: ["service"],
  settingsSections: [
    {
      key: "services",
      label: "Services",
      path: "services",
      group: "Catalog & pricing",
      order: 200,
      icon: Package,
      description: "Your price list, with SAC codes and GST rates",
      element: section(ServicesPage),
    },
    {
      key: "packages",
      label: "Packages",
      path: "packages",
      group: "Catalog & pricing",
      order: 210,
      icon: Boxes,
      description: "Bundles you sell as one thing, with deliverable quotas",
      element: section(PackagesPage),
    },
    {
      key: "gst-rates",
      label: "GST rates",
      path: "gst-rates",
      group: "Catalog & pricing",
      order: 220,
      icon: Percent,
      description: "The tax rates this workspace bills with",
      element: section(TaxRatesPage),
    },
    {
      key: "rate-cards",
      label: "Rate cards",
      path: "rate-cards",
      group: "Catalog & pricing",
      order: 230,
      icon: Wallet,
      permission: "financials:view",
      description: "What an hour of your team's time is billed at",
      element: section(RateCardsPage),
    },
  ],
  commands: [
    {
      key: "catalog.services",
      label: "Service catalog",
      icon: Package,
      to: "/settings/services",
      group: "navigate",
      keywords: ["price list", "prices", "sac", "rate", "catalogue"],
      description: "Your price list, with SAC codes and GST rates",
    },
    {
      key: "catalog.packages",
      label: "Packages",
      icon: Boxes,
      to: "/settings/packages",
      group: "navigate",
      keywords: ["bundle", "retainer package", "plan", "tier"],
    },
    {
      key: "catalog.gst",
      label: "GST rates",
      icon: Percent,
      to: "/settings/gst-rates",
      group: "navigate",
      keywords: ["tax", "slab", "18%", "exempt", "cgst", "sgst", "igst"],
    },
    {
      key: "catalog.rate-cards",
      label: "Rate cards",
      icon: Wallet,
      to: "/settings/rate-cards",
      permission: "financials:view",
      group: "navigate",
      keywords: ["bill rate", "hourly", "cost", "profitability", "utilisation"],
    },
  ],
});
